// ---------------------------------------------------------------------------
// Venue definitions for the facility booking system.
//
// To add, remove, or rename a venue: edit the VENUES array below.
// The `id` field is stored in the database — do not change it after launch.
//
// `resourceEmail` is OPTIONAL. Once you have created a Resource Mailbox in M365
// Admin Center for a venue, uncomment and set its email address here.
// Approved bookings will then automatically create a calendar event on that mailbox,
// and any staff subscribed (or added as attendees) will see it in their Outlook.
// ---------------------------------------------------------------------------

export interface VenueSpace {
  id: string;
  label: string;
  /**
   * Space IDs within the same venue that cannot be booked at the same time.
   * E.g. "full-hall" conflicts with ["court-1","court-2","court-3"] and vice-versa.
   */
  conflictsWith?: string[];
}

export interface VenueAddon {
  id: string;
  label: string;
  /** If true, selecting this addon is an enquiry only — not a guaranteed service. */
  enquiryOnly?: boolean;
}

export type VenueCategory = "indoor" | "outdoor";

export interface Venue {
  id: string;
  name: string;
  category: VenueCategory;
  description: string;
  capacity: number;
  amenities: string[];
  /**
   * Outlook Resource Mailbox email (optional).
   * Set this once you have created the mailbox in M365 Admin Center.
   * The Graph API will create a calendar event here on approval.
   */
  resourceEmail?: string;
  /** Bookings are only accepted within these hours (24-h, Africa/Johannesburg) */
  operatingHours: { start: string; end: string };
  /** Max booking duration in hours */
  maxBookingHours: number;
  /** Sub-spaces within this venue that guests must choose from (e.g. individual courts). */
  spaces?: VenueSpace[];
  /** Optional add-on services guests can request (e.g. Sound System). */
  addons?: VenueAddon[];
}

export const VENUES: Venue[] = [
  {
    id: "legacy-hall",
    name: "Legacy Hall",
    category: "indoor",
    description:
      "Our main auditorium, ideal for events, performances, prize-givings, and large community gatherings.",
    capacity: 500,
    amenities: ["Stage", "Sound System", "4K Projector", "Air Conditioning", "PA System", "Dressing Rooms"],
    resourceEmail: "legacyhall@ashtonjohnsschool.co.za",
    operatingHours: { start: "07:00", end: "21:00" },
    maxBookingHours: 8,
    spaces: [
      { id: "court-1", label: "Court 1 (Netball / Indoor)", conflictsWith: ["full-hall"] },
      { id: "court-2", label: "Court 2 (Netball / Indoor)", conflictsWith: ["full-hall"] },
      { id: "court-3", label: "Court 3 (Netball / Indoor)", conflictsWith: ["full-hall"] },
      { id: "full-hall", label: "Full Hall / Indoor Soccer Field", conflictsWith: ["court-1", "court-2", "court-3"] },
    ],
    addons: [
      { id: "sound-system", label: "Sound System", enquiryOnly: true },
      { id: "projector", label: "4K Projector", enquiryOnly: true },
    ],
  },
  {
    id: "netball-court",
    name: "Netball / Tennis Courts",
    category: "outdoor",
    description:
      "Full-size outdoor courts available for netball matches, tennis, training sessions, and community sport.",
    capacity: 200,
    amenities: ["Floodlights", "Change Rooms", "Ablution Facilities"],
    resourceEmail: "netball@ashtonjohnsschool.co.za",
    operatingHours: { start: "06:00", end: "20:00" },
    maxBookingHours: 6,
    spaces: [
      { id: "netball-1", label: "Court 1 (Netball / Outdoor)", conflictsWith: ["tennis-1", "all-courts-netball", "all-courts-tennis"] },
      { id: "netball-2", label: "Court 2 (Netball / Outdoor)", conflictsWith: ["tennis-2", "all-courts-netball", "all-courts-tennis"] },
      { id: "tennis-1",  label: "Court 1 (Tennis / Outdoor)",  conflictsWith: ["netball-1", "all-courts-netball", "all-courts-tennis"] },
      { id: "tennis-2",  label: "Court 2 (Tennis / Outdoor)",  conflictsWith: ["netball-2", "all-courts-netball", "all-courts-tennis"] },
      { id: "all-courts-netball", label: "All Courts (Netball / Outdoor)", conflictsWith: ["netball-1", "netball-2", "tennis-1", "tennis-2", "all-courts-tennis"] },
      { id: "all-courts-tennis",  label: "All Courts (Tennis / Outdoor)",  conflictsWith: ["netball-1", "netball-2", "tennis-1", "tennis-2", "all-courts-netball"] },
    ],
  },
  {
    id: "soccer-field",
    name: "Soccer Field",
    category: "outdoor",
    description:
      "Two full-size outdoor soccer fields available for matches, training sessions, and field events.",
    capacity: 1000,
    amenities: ["Floodlights", "Change Rooms", "Ablution Facilities", "Spectator Stands"],
    resourceEmail: "soccerfield@ashtonjohnsschool.co.za",
    operatingHours: { start: "06:00", end: "20:00" },
    maxBookingHours: 8,
    spaces: [
      { id: "senior-field", label: "Senior Field" },
      { id: "junior-field", label: "Junior Field" },
    ],
  },
  {
    id: "cafeteria",
    name: "Cafeteria",
    category: "indoor",
    description:
      "Spacious dining hall suitable for community breakfasts, fundraisers, and seated functions.",
    capacity: 150,
    amenities: ["Tables & Chairs", "Kitchen Access", "Air Conditioning", "PA System"],
    resourceEmail: "cafeteria@ashtonjohnsschool.co.za",
    operatingHours: { start: "07:00", end: "18:00" },
    maxBookingHours: 6,
    addons: [
      { id: "tuckshop", label: "Tuckshop Services", enquiryOnly: true },
      { id: "sound-system", label: "Sound System", enquiryOnly: true },
      { id: "projector", label: "Projector", enquiryOnly: true },
    ],
  },
  // ── Add more venues below as they are confirmed ───────────────────────────
  // {
  //   id: "venue-id",           ← lowercase, hyphens only, never change after launch
  //   name: "Venue Name",
  //   description: "...",
  //   capacity: 100,
  //   amenities: [],
  //   // resourceEmail: "venue@ashtonjohnsschool.co.za",
  //   operatingHours: { start: "07:00", end: "17:00" },
  //   maxBookingHours: 4,
  // },
];

export function getVenueById(id: string): Venue | undefined {
  return VENUES.find((v) => v.id === id);
}
